/**
 * Form data cleaning utilities.
 */

/**
 * Clean form data to prevent common database errors
 * Handles empty strings, null values, and type conversions
 */
export function cleanFormData<T extends object>(data: T): T {
  // A plain form object is structurally a string-keyed record at runtime; this
  // single audited cast lets typed interface callers (which lack an index
  // signature) pass without forcing `as` at every call site. Return type stays `T`.
  const cleaned: Record<string, unknown> = { ...(data as Record<string, unknown>) };

  // Process each field
  Object.keys(cleaned).forEach((key) => {
    const value = cleaned[key];

    // Handle numeric fields - convert empty strings to undefined
    if (
      typeof value === "string" &&
      (key.includes("_rate") || key.includes("_hours") || key.includes("_amount"))
    ) {
      cleaned[key] = value.trim() === "" ? undefined : Number(value);
    }

    // Handle optional text fields - convert empty strings to undefined
    else if (
      typeof value === "string" &&
      (key.includes("_notes") ||
        key.includes("_description") ||
        key.endsWith("_id") ||
        key === "phone" ||
        key === "website" ||
        key === "address")
    ) {
      cleaned[key] = value.trim() || undefined;
    }

    // Handle arrays - remove empty values
    else if (Array.isArray(value)) {
      cleaned[key] =
        value.length > 0 ? value.filter((item) => item && String(item).trim()) : undefined;
    }

    // Handle date strings
    else if (typeof value === "string" && key.includes("_date")) {
      cleaned[key] = value || undefined;
    }
  });

  return cleaned as T;
}
